import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * AUTH REDESIGN PREVIEW — futuristic gaming/HUD aesthetic
 * Public preview at /design-preview so the user can decide before we rewrite production pages.
 *
 * Tokens (from /app/design_guidelines.json):
 *   bg: #050505, card: #0A0B10, input: #000000
 *   primary: #00F0FF (cyan), secondary: #FF007F (magenta), success: #39FF14
 *   fonts: Rajdhani (headings), IBM Plex Mono (body/labels)
 */

// ============================================================
// Palettes — four non-black variants the user can toggle
// ============================================================
const PALETTES = {
  daylight: {
    name: "Daylight Light",
    blurb: "Near-white cream + navy text · solid orange buttons. Crisp & professional.",
    bg: "#FCFAF3",
    bg2: "#F5F1E0",
    card: "rgba(255, 255, 255, 0.95)",
    input: "#FFFFFF",
    border: "#E5DCC0",
    primary: "#F77F00",
    primaryDark: "#D26900",
    primaryGlow: "rgba(247,127,0,0.45)",
    secondary: "#D62828",
    success: "#003049",
    overlay1: "rgba(247,127,0,0.08)",
    overlay2: "rgba(0,48,73,0.05)",
    text: "#003049",
    textMuted: "rgba(0,48,73,0.62)",
    textOnPrimary: "#FFFFFF",
    cream: "#EAE2B7",
    light: true,
  },
  warmcream: {
    name: "Daylight Warm",
    blurb: "Richer cream + navy text · solid orange buttons. Warmer paper feel.",
    bg: "#EFE6CC",
    bg2: "#E5D9B5",
    card: "rgba(252, 248, 235, 0.95)",
    input: "#FBF6E6",
    border: "#C8B98C",
    primary: "#F77F00",
    primaryDark: "#D26900",
    primaryGlow: "rgba(247,127,0,0.50)",
    secondary: "#D62828",
    success: "#003049",
    overlay1: "rgba(247,127,0,0.14)",
    overlay2: "rgba(214,40,40,0.06)",
    text: "#003049",
    textMuted: "rgba(0,48,73,0.62)",
    textOnPrimary: "#FFFFFF",
    cream: "#EAE2B7",
    light: true,
  },
  ember: {
    name: "Ember Cockpit",
    blurb: "Deep navy cockpit with red→orange→gold→cream sunset glow. Warm + premium.",
    bg: "#003049",
    bg2: "#00263A",
    card: "rgba(0, 48, 73, 0.82)",
    input: "#001E2E",
    border: "#1B4A66",
    primary: "#F77F00",
    primaryDark: "#D26900",
    primaryGlow: "rgba(247,127,0,0.55)",
    secondary: "#D62828",
    success: "#FCBF49",
    overlay1: "rgba(247,127,0,0.22)",
    overlay2: "rgba(214,40,40,0.14)",
    cream: "#EAE2B7",
    text: "#FFFFFF",
    textMuted: "rgba(255,255,255,0.65)",
    textOnPrimary: "#003049",
  },
  midnight: {
    name: "Midnight Indigo",
    blurb: "Deep indigo navy + electric cyan. Cohesive with our brand navy.",
    bg: "#0B1437",
    bg2: "#1A1F4B",
    card: "rgba(15, 23, 60, 0.78)",
    input: "#0A1230",
    border: "#27325C",
    primary: "#00F0FF",
    primaryDark: "#00BCC8",
    primaryGlow: "rgba(0,240,255,0.55)",
    secondary: "#FF66D9",
    success: "#39FF14",
    overlay1: "rgba(0,240,255,0.18)",
    overlay2: "rgba(255,102,217,0.14)",
    text: "#FFFFFF",
    textMuted: "rgba(255,255,255,0.65)",
    textOnPrimary: "#0B1437",
  },
  synthwave: {
    name: "Synthwave Sunset",
    blurb: "Deep violet → magenta with hot-pink + cyan neons. Retro-future.",
    bg: "#1F0B3D",
    bg2: "#3D0F4A",
    card: "rgba(40, 14, 70, 0.78)",
    input: "#22093E",
    border: "#5A2C7A",
    primary: "#FF2BD6",
    primaryGlow: "rgba(255,43,214,0.55)",
    secondary: "#00F0FF",
    success: "#FFD600",
    overlay1: "rgba(255,43,214,0.20)",
    overlay2: "rgba(0,240,255,0.14)",
    text: "#FFFFFF", textMuted: "rgba(255,255,255,0.65)", textOnPrimary: "#1F0B3D",
  },
  ocean: {
    name: "Deep Ocean",
    blurb: "Sci-fi underwater teal with mint and aqua neons. Calm + powerful.",
    bg: "#062E3F",
    bg2: "#0A4759",
    card: "rgba(8, 50, 67, 0.78)",
    input: "#062632",
    border: "#1C5165",
    primary: "#00FFC2",
    primaryGlow: "rgba(0,255,194,0.55)",
    secondary: "#FFD600",
    success: "#39FF14",
    overlay1: "rgba(0,255,194,0.20)",
    overlay2: "rgba(255,214,0,0.10)",
    text: "#FFFFFF", textMuted: "rgba(255,255,255,0.65)", textOnPrimary: "#062E3F",
  },
  plasma: {
    name: "Plasma Royal",
    blurb: "Royal purple with hot magenta + cool cyan glow. Premium gaming UI.",
    bg: "#180B3D",
    bg2: "#2A0F5C",
    card: "rgba(34, 12, 80, 0.78)",
    input: "#160938",
    border: "#3A1F70",
    primary: "#B85CFF",
    primaryGlow: "rgba(184,92,255,0.55)",
    secondary: "#00F0FF",
    success: "#FF66D9",
    overlay1: "rgba(184,92,255,0.20)",
    overlay2: "rgba(0,240,255,0.14)",
    text: "#FFFFFF", textMuted: "rgba(255,255,255,0.65)", textOnPrimary: "#180B3D",
  },
};

const PaletteCtx = React.createContext(PALETTES.daylight);
const usePal = () => React.useContext(PaletteCtx);

// ============================================================
// Audio: futuristic click via Web Audio API (no asset needed)
// ============================================================
function useClickSound() {
  const ctxRef = useRef(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
      catch (e) { return null; }
    }
    return ctxRef.current;
  };
  return (variant = "primary") => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(4000, now);

    if (variant === "primary") {
      osc.type = "square";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.06);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    } else if (variant === "hover") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.04, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    } else { // confirm
      osc.type = "triangle";
      osc.frequency.setValueAtTime(660, now);
      osc.frequency.exponentialRampToValueAtTime(990, now + 0.1);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.22, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    }
    osc.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  };
}

// ============================================================
// Reusable HUD primitives
// ============================================================
const NeonButton = ({ children, variant = "primary", onClick, type = "button", className = "", style, ...rest }) => {
  const play = useClickSound();
  const pal = usePal();
  const base = "relative inline-flex items-center justify-center gap-2 h-14 px-8 font-bold uppercase tracking-[0.25em] text-sm rounded-sm transition-all duration-300 active:scale-[0.97] select-none";
  const styles = {
    primary: {
      background: pal.primary,
      border: `2px solid ${pal.primary}`,
      color: pal.textOnPrimary,
      boxShadow: `0 0 18px ${pal.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.18)`,
    },
    secondary: {
      background: "transparent",
      border: `2px solid ${pal.border}`,
      color: pal.textMuted,
    },
    ghost: { background: "transparent", border: "none", color: pal.textMuted },
  };
  const isPrimary = variant === "primary";
  const isSecondary = variant === "secondary";
  return (
    <button
      type={type}
      onClick={(e) => { play("primary"); onClick && onClick(e); }}
      className={`${base} ${className}`}
      style={{ ...styles[variant], ...style }}
      onMouseEnter={(e) => {
        if (isPrimary) {
          e.currentTarget.style.background = pal.primaryDark || pal.primary;
          e.currentTarget.style.borderColor = pal.primaryDark || pal.primary;
          e.currentTarget.style.color = pal.textOnPrimary;
          e.currentTarget.style.boxShadow = `0 0 32px ${pal.primaryGlow}, inset 0 0 14px rgba(255,255,255,0.25)`;
          e.currentTarget.style.transform = "translateY(-1px) scale(1.01)";
        } else if (isSecondary) {
          e.currentTarget.style.borderColor = pal.secondary;
          e.currentTarget.style.color = pal.secondary;
          e.currentTarget.style.boxShadow = `0 0 20px ${pal.primaryGlow.replace("0.55", "0.30").replace("0.45", "0.25").replace("0.50", "0.28")}`;
        }
      }}
      onMouseLeave={(e) => {
        Object.assign(e.currentTarget.style, styles[variant], style || {});
        e.currentTarget.style.transform = "";
      }}
      {...rest}
    >
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </button>
  );
};

const NeonInput = ({ label, type = "text", placeholder, icon, ...rest }) => {
  const pal = usePal();
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] mb-2 font-mono" style={{ color: pal.primary }}>{label}</div>
      <div className="relative">
        {icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" style={{ color: pal.text }}>{icon}</div>}
        <input
          type={type}
          placeholder={placeholder}
          className={`w-full h-14 rounded-sm font-mono px-4 ${icon ? "pl-11" : ""} outline-none transition-all`}
          style={{
            background: pal.input,
            border: `2px solid ${pal.border}`,
            color: pal.text,
          }}
          onFocus={(e) => { e.target.style.borderColor = pal.primary; e.target.style.boxShadow = `0 0 18px ${pal.primaryGlow.replace("0.55", "0.35").replace("0.45", "0.30")}`; }}
          onBlur={(e) => { e.target.style.borderColor = pal.border; e.target.style.boxShadow = "none"; }}
          {...rest}
        />
      </div>
    </label>
  );
};

const HudFrame = ({ children, className = "" }) => {
  const pal = usePal();
  return (
    <div className={`relative ${className}`}>
      <span className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2" style={{borderColor: pal.primary}} />
      <span className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2" style={{borderColor: pal.primary}} />
      <span className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2" style={{borderColor: pal.primary}} />
      <span className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2" style={{borderColor: pal.primary}} />
      {children}
    </div>
  );
};

const ScanlineBG = () => {
  const pal = usePal();
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 opacity-25" style={{
        background: `repeating-linear-gradient(0deg, ${pal.primaryGlow.replace("0.55","0.06")} 0px, ${pal.primaryGlow.replace("0.55","0.06")} 1px, transparent 1px, transparent 3px)`,
      }} />
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 18%, ${pal.overlay1}, transparent 45%), radial-gradient(circle at 82% 75%, ${pal.overlay2}, transparent 50%)`
      }} />
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: `linear-gradient(${pal.primary} 1px, transparent 1px), linear-gradient(90deg, ${pal.primary} 1px, transparent 1px)`,
        backgroundSize: "60px 60px"
      }} />
    </div>
  );
};

// Boot text animation
const BootLines = ({ lines }) => {
  const pal = usePal();
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setShown((s) => Math.min(s + 1, lines.length)), 220);
    return () => clearInterval(t);
  }, [lines.length]);
  return (
    <div className="font-mono text-[11px] space-y-1" style={{ color: pal.primary, opacity: 0.85 }}>
      {lines.slice(0, shown).map((l, i) => (
        <div key={i} className="flex gap-2">
          <span style={{ color: pal.success }}>{">"}</span><span>{l}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// SCREEN: Landing
// ============================================================
function LandingScreen({ onNav }) {
  const pal = usePal();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] min-h-[640px] relative">
      {/* LEFT — simulator HUD */}
      <div className="relative overflow-hidden p-10 lg:p-14 flex flex-col justify-between"
        style={{ background: `linear-gradient(135deg, ${pal.bg} 0%, ${pal.bg2} 60%, ${pal.bg} 100%)` }}>
        <ScanlineBG />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-9 h-9 rounded-sm border-2 flex items-center justify-center font-display font-bold"
              style={{ borderColor: pal.primary, color: pal.primary, boxShadow: `0 0 14px ${pal.primaryGlow}` }}>L</div>
            <div className="font-display text-2xl tracking-[0.25em]" style={{ color: pal.text }}>LINGOQ</div>
            <div className="ml-2 text-[10px] font-mono tracking-widest" style={{ color: pal.success }}>// ONLINE</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-4" style={{ color: pal.primary, opacity: 0.8 }}>// neural training core v2.6</div>
          <h1 className="font-display font-bold text-5xl lg:text-6xl leading-[0.95] tracking-tight" style={{ color: pal.text }}>
            INITIALIZE<br />
            <span style={{ color: pal.primary, textShadow: `0 0 22px ${pal.primaryGlow}` }}>SIMULATION</span>
          </h1>
          <p className="font-mono text-sm mt-6 max-w-md leading-relaxed" style={{ color: pal.textMuted }}>
            Enter the high-stakes English prep arena. Real-time AI examiners, predictive band scoring, and full IELTS / PTE / Duolingo / Oxford ELLT realism — engineered like a fighter-pilot simulator.
          </p>
        </div>

        <div className="relative grid grid-cols-3 gap-3 mt-10">
          {[
            ["27,400", "RECRUITS"],
            ["±0.5", "BAND PRECISION"],
            ["24/7", "AI EXAMINERS"],
          ].map(([n, l], i) => (
            <HudFrame key={i} className="p-4">
              <div className="font-display text-3xl tracking-tight" style={{ color: pal.text }}>{n}</div>
              <div className="font-mono text-[9px] tracking-[0.3em] mt-1" style={{ color: pal.primary }}>{l}</div>
            </HudFrame>
          ))}
        </div>
      </div>

      {/* RIGHT — entry panel */}
      <div className="relative flex items-center justify-center p-10 lg:p-14"
        style={{ background: pal.bg, borderLeft: `1px solid ${pal.border}` }}>
        <ScanlineBG />
        <HudFrame className="relative w-full max-w-md backdrop-blur-xl p-8 lg:p-10"
          style={{ background: pal.card, border: `1px solid ${pal.border}` }}>
          <div className="font-mono text-[10px] tracking-[0.3em] mb-1" style={{ color: pal.success }}>// ACCESS PROTOCOL</div>
          <div className="font-display text-3xl tracking-tight mb-2" style={{ color: pal.text }}>CHOOSE ENTRY POINT</div>
          <p className="font-mono text-xs mb-8 leading-relaxed" style={{ color: pal.textMuted }}>Recruits create a new training profile. Veterans authenticate to resume mission.</p>

          <div className="space-y-3">
            <NeonButton variant="primary" onClick={() => onNav("signup")} className="w-full" data-testid="preview-cta-signup">
              ▸ START YOUR PREP
            </NeonButton>
            <NeonButton variant="secondary" onClick={() => onNav("login")} className="w-full" data-testid="preview-cta-login">
              ◂ I HAVE AN ACCOUNT
            </NeonButton>
          </div>

          <div className="mt-8 pt-6 border-t" style={{ borderColor: pal.border }}>
            <BootLines lines={[
              "checking neural link…",
              "loading rubric matrices…",
              "examiner array online.",
              "awaiting candidate input ▌",
            ]} />
          </div>
        </HudFrame>
      </div>
    </div>
  );
}

// ============================================================
// SCREEN: Signup
// ============================================================
function SignupScreen({ onNav }) {
  return (
    <AuthCardScreen
      tag="// NEW RECRUIT"
      title="CREATE PROFILE"
      subtitle="A new training file will be initialised in our neural core."
      fields={[
        { label: "Full Name", placeholder: "AGENT_ALEX_KAUR", testid: "preview-signup-name" },
        { label: "Email Address", placeholder: "alex@quickmail.com", type: "email", testid: "preview-signup-email" },
        { label: "Password", placeholder: "•••••••••", type: "password", testid: "preview-signup-pwd" },
      ]}
      cta="CREATE PROFILE"
      ctaTestid="preview-signup-submit"
      footerLeft="ALREADY REGISTERED?"
      footerLink="LOGIN"
      onFooterClick={() => onNav("login")}
    />
  );
}

// ============================================================
// SCREEN: Login
// ============================================================
function LoginScreen({ onNav }) {
  const pal = usePal();
  // Update login extra section labels to use palette text
  const loginExtra = (
    <div className="flex items-center justify-between mt-2 font-mono text-[11px]" style={{ color: pal.textMuted || "#9aa1c2" }}>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="w-4 h-4" style={{ accentColor: pal.primary }} /> REMEMBER ME
      </label>
      <button onClick={() => onNav("forgot")} className="uppercase tracking-[0.25em] transition-colors"
        onMouseEnter={(e) => e.currentTarget.style.color = pal.secondary}
        onMouseLeave={(e) => e.currentTarget.style.color = ""}>FORGOT?</button>
    </div>
  );
  return (
    <AuthCardScreen
      tag="// SYSTEM LOGIN"
      title="AUTHENTICATE"
      subtitle="Re-engage your training session. Examiners are standing by."
      fields={[
        { label: "Email Address", placeholder: "alex@quickmail.com", type: "email", testid: "preview-login-email" },
        { label: "Password", placeholder: "•••••••••", type: "password", testid: "preview-login-pwd" },
      ]}
      cta="AUTHENTICATE"
      ctaTestid="preview-login-submit"
      footerLeft="NO PROFILE YET?"
      footerLink="SIGN UP"
      onFooterClick={() => onNav("signup")}
      extra={loginExtra}
    />
  );
}

// ============================================================
// SCREEN: Forgot Password
// ============================================================
function ForgotScreen({ onNav }) {
  return (
    <AuthCardScreen
      tag="// RECOVERY MODE"
      title="RESET PROTOCOL"
      subtitle="We will dispatch a one-time access key to your registered comm channel."
      fields={[
        { label: "Email Address", placeholder: "alex@quickmail.com", type: "email", testid: "preview-forgot-email" },
      ]}
      cta="SEND RESET PROTOCOL"
      ctaTestid="preview-forgot-submit"
      footerLeft="REGAINED ACCESS?"
      footerLink="RETURN TO LOGIN"
      onFooterClick={() => onNav("login")}
    />
  );
}

function AuthCardScreen({ tag, title, subtitle, fields, cta, ctaTestid, footerLeft, footerLink, onFooterClick, extra }) {
  const pal = usePal();
  return (
    <div className="relative min-h-[640px] flex items-center justify-center p-6 lg:p-12"
      style={{ background: `linear-gradient(135deg, ${pal.bg} 0%, ${pal.bg2} 70%, ${pal.bg} 100%)` }}>
      <ScanlineBG />
      <HudFrame className="relative w-full max-w-md backdrop-blur-xl p-8 lg:p-10"
        style={{ background: pal.card, border: `1px solid ${pal.border}` }}>
        <div className="font-mono text-[10px] tracking-[0.3em] mb-1" style={{ color: pal.success }}>{tag}</div>
        <div className="font-display text-3xl tracking-tight mb-2" style={{ color: pal.text }}>{title}</div>
        <p className="font-mono text-xs mb-8 leading-relaxed" style={{ color: pal.textMuted }}>{subtitle}</p>

        <div className="space-y-5">
          {fields.map((f, i) => (
            <NeonInput key={i} label={f.label} placeholder={f.placeholder} type={f.type || "text"} data-testid={f.testid} />
          ))}
        </div>

        {extra}

        <NeonButton variant="primary" className="w-full mt-7" data-testid={ctaTestid}>▸ {cta}</NeonButton>

        <div className="mt-7 pt-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] border-t" style={{ borderColor: pal.border, color: pal.textMuted }}>
          <span>{footerLeft}</span>
          <button onClick={onFooterClick} className="transition-colors" style={{ color: pal.primary }}
            onMouseEnter={(e) => e.currentTarget.style.color = pal.secondary}
            onMouseLeave={(e) => e.currentTarget.style.color = pal.primary}>
            {footerLink} →
          </button>
        </div>
      </HudFrame>
    </div>
  );
}

// ============================================================
// SCREEN: Onboarding (step 1 of 5 — exam target picker)
// ============================================================
function OnboardingScreen() {
  const [picked, setPicked] = useState(null);
  const play = useClickSound();
  const pal = usePal();
  const exams = [
    { id: "IELTS", name: "IELTS",     sub: "Academic / General",     accent: pal.primary },
    { id: "PTE",   name: "PTE",       sub: "Pearson Academic",       accent: pal.secondary },
    { id: "ELLT",  name: "OXFORD ELLT", sub: "University admissions", accent: pal.success },
    { id: "DUO",   name: "DUOLINGO",  sub: "Adaptive English Test",  accent: "#FFD600" },
  ];
  return (
    <div className="relative min-h-[640px] p-8 lg:p-12"
      style={{ background: `linear-gradient(135deg, ${pal.bg} 0%, ${pal.bg2} 70%, ${pal.bg} 100%)` }}>
      <ScanlineBG />
      <div className="relative max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-3 font-mono text-[10px] tracking-[0.3em]" style={{ color: pal.textMuted }}>
          <span style={{ color: pal.success }}>// MISSION CONFIG · STEP 01 / 05</span>
          <span>EST. 02:00 MIN</span>
        </div>
        <div className="flex gap-2 mb-10">
          {[0,1,2,3,4].map((i) => (
            <div key={i} className="flex-1 h-1.5 rounded-sm"
              style={{ background: i === 0 ? pal.primary : pal.border, boxShadow: i === 0 ? `0 0 10px ${pal.primaryGlow}` : "none" }} />
          ))}
        </div>

        <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ color: pal.text }}>SELECT TARGET PARAMETERS</h1>
        <p className="font-mono text-sm mt-3 mb-10" style={{ color: pal.textMuted }}>Which exam are you training for? Tap a cartridge to load its rubric matrix.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {exams.map((e) => {
            const active = picked === e.id;
            return (
              <button
                key={e.id}
                onClick={() => { play("primary"); setPicked(e.id); }}
                data-testid={`preview-exam-${e.id.toLowerCase()}`}
                className={`group relative text-left rounded-sm border-2 p-6 transition-all duration-300 ${active ? "" : "hover:-translate-y-1"}`}
                style={{
                  borderColor: active ? e.accent : pal.border,
                  background: active ? `linear-gradient(135deg, ${e.accent}22, transparent 60%), ${pal.card}` : pal.card,
                  boxShadow: active ? `0 0 26px ${e.accent}80` : "none",
                }}
              >
                <span className="absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2" style={{borderColor: active ? e.accent : pal.border}} />
                <span className="absolute -top-1 -right-1 w-3 h-3 border-t-2 border-r-2" style={{borderColor: active ? e.accent : pal.border}} />
                <span className="absolute -bottom-1 -left-1 w-3 h-3 border-b-2 border-l-2" style={{borderColor: active ? e.accent : pal.border}} />
                <span className="absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2" style={{borderColor: active ? e.accent : pal.border}} />
                <div className="font-mono text-[10px] tracking-[0.3em]" style={{color: e.accent}}>{`[ ${active ? "■" : "□"} ]`}</div>
                <div className="font-display text-2xl mt-3 tracking-tight" style={{ color: pal.text }}>{e.name}</div>
                <div className="font-mono text-xs mt-1" style={{ color: pal.textMuted }}>{e.sub}</div>
                <div className="font-mono text-[10px] mt-6" style={{ color: pal.textMuted, opacity: 0.55 }}>CARTRIDGE_{e.id}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <div className="font-mono text-[11px] tracking-[0.25em]" style={{ color: pal.textMuted }}>← BACK TO ACCESS</div>
          <NeonButton variant="primary" disabled={!picked} data-testid="preview-onboarding-confirm" className={!picked ? "opacity-40 cursor-not-allowed" : ""}>
            CONFIRM SELECTION ▸
          </NeonButton>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN: tab switcher
// ============================================================
const TABS = [
  { id: "landing", label: "Landing" },
  { id: "signup", label: "Sign-up" },
  { id: "login", label: "Login" },
  { id: "forgot", label: "Forgot" },
  { id: "onboarding", label: "Onboarding 1/5" },
];

export default function DesignPreview() {
  const [tab, setTab] = useState("landing");
  const [palKey, setPalKey] = useState("daylight");
  const pal = PALETTES[palKey];
  const play = useClickSound();

  // Inject futuristic fonts once
  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);

  const screen = (() => {
    switch (tab) {
      case "signup": return <SignupScreen onNav={setTab} />;
      case "login": return <LoginScreen onNav={setTab} />;
      case "forgot": return <ForgotScreen onNav={setTab} />;
      case "onboarding": return <OnboardingScreen />;
      default: return <LandingScreen onNav={setTab} />;
    }
  })();

  return (
    <PaletteCtx.Provider value={pal}>
      <div className="min-h-screen" style={{ fontFamily: "'IBM Plex Mono', monospace", background: pal.bg, color: pal.text }} data-testid="design-preview-root">
        <style>{`
          [data-preview] h1, [data-preview] h2, .font-display { font-family: 'Rajdhani', sans-serif !important; }
          [data-preview] input::placeholder { color: ${pal.textMuted}; opacity: 0.6; }
        `}</style>

        {/* Top admin bar */}
        <div className="sticky top-0 z-20 backdrop-blur border-b px-4 py-3 flex items-center justify-between flex-wrap gap-3"
          style={{ background: `${pal.bg}E6`, borderColor: pal.border }}>
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-sm border-2 flex items-center justify-center font-bold text-xs"
              style={{ borderColor: pal.primary, color: pal.primary, boxShadow: `0 0 10px ${pal.primaryGlow}`, fontFamily:"'Rajdhani', sans-serif" }}>L</div>
            <div className="font-mono text-[11px] tracking-[0.3em]" style={{ color: pal.textMuted }}>LINGOQ · AUTH REDESIGN PREVIEW</div>
          </div>

          {/* Palette switcher */}
          <div className="flex gap-1 flex-wrap items-center">
            <div className="font-mono text-[9px] tracking-[0.3em] mr-1" style={{ color: pal.textMuted }}>PALETTE:</div>
            {Object.entries(PALETTES).map(([k, p]) => (
              <button
                key={k}
                onClick={() => { play("hover"); setPalKey(k); }}
                data-testid={`preview-palette-${k}`}
                title={p.blurb}
                className="px-3 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.2em] border-2 transition-all"
                style={{
                  borderColor: palKey === k ? p.primary : pal.border,
                  color: palKey === k ? p.primary : pal.textMuted,
                  background: palKey === k ? `${p.primary}1A` : "transparent",
                  boxShadow: palKey === k ? `0 0 10px ${p.primaryGlow}` : "none",
                }}
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full align-middle mr-1.5" style={{ background: p.primary, boxShadow: `0 0 6px ${p.primary}` }} />
                {p.name.split(" ")[0]}
              </button>
            ))}
          </div>

          <div className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => { play("hover"); setTab(t.id); }}
                data-testid={`preview-tab-${t.id}`}
                className="px-4 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.25em] border-2 transition-all"
                style={{
                  borderColor: tab === t.id ? pal.primary : pal.border,
                  color: tab === t.id ? pal.primary : pal.textMuted,
                  background: tab === t.id ? `${pal.primary}1A` : "transparent",
                  boxShadow: tab === t.id ? `0 0 12px ${pal.primaryGlow}` : "none",
                }}
              >
                {t.label}
              </button>
            ))}
            <Link to="/login" className="ml-3 px-4 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.25em] border-2 transition-colors"
              style={{ borderColor: pal.border, color: pal.textMuted }}>
              ← back to live app
            </Link>
          </div>
        </div>

        {/* Palette tagline */}
        <div className="px-4 py-2 font-mono text-[10px] tracking-[0.3em] border-b" style={{ borderColor: pal.border, background: `${pal.bg2}40`, color: pal.textMuted }}>
          // {pal.name.toUpperCase()} — {pal.blurb}
        </div>

        <div data-preview key={`${tab}-${palKey}`} className="animate-fadein">
          {screen}
        </div>

        <style>{`
          @keyframes fadein { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
          .animate-fadein { animation: fadein 0.35s ease-out both }
        `}</style>

        <div className="border-t px-4 py-4 text-center font-mono text-[11px] tracking-[0.2em]" style={{ borderColor: pal.border, background: pal.bg, color: pal.textMuted }}>
          // CLICK ANY BUTTON OR TAB TO HEAR THE FUTURISTIC CLICK SOUND ·
          FONTS: RAJDHANI + IBM PLEX MONO ·
          TRY THE 5 PALETTES ABOVE
        </div>
      </div>
    </PaletteCtx.Provider>
  );
}
